import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import SidebarLayout from '../../../../layouts/SidebarLayout';
import sidebar from '../../utils/sidebar';
import PageSubHeader from '../../../../components/Layouts/PageSubHeader';
import DefaultButton from '../../../../components/Buttons/Default';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline, IoTrashOutline } from 'react-icons/io5';
import { BsTrash3 } from 'react-icons/bs';
import { TfiFilter } from 'react-icons/tfi';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import { del, get } from '../../../../utils/helpers/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useBranch } from "../../../../context/branch";
import { useSettings } from '../../../../context/settings';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import FiltersDrawer from './Drawers/Filters';
import { printOrderStatus } from '../../utils/helpers';
import ViewDrawer from './Drawers/View';
import AgGridTable from '../../../../components/Tables/AgGrid';
import i18n from '../../../../i18n';

ModuleRegistry.registerModules([AllCommunityModule]);

const STATUS_LABELS = {
  draft: i18n.t('common.draft'),
  sent: i18n.t('common.sent'),
  approved: i18n.t('common.approved'),
  received: i18n.t('common.received'),
  canceled: i18n.t('common.canceled')
};

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/purchases/purchase-orders/edit/${data.id}`, { state: { orderData: data } });
        }}
        size="sm"
        outline
        color="secondary"
      >
        <IoCreateOutline />
      </IconButton>

      {data.status !== 'received' && data.status !== 'canceled' && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            context.setOrderToDelete(data.id);
          }}
          size="sm"
          outline
          color="danger"
        >
          <BsTrash3 />
        </IconButton>
      )}
    </div>
  );
}

function OrdersPage() {
  const { t } = useTranslation();
  const { isMultiBranch, allBranchesMode } = useBranch();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['order_number', 'status', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(Boolean).length;

  const getOrders = useCallback(async () => {
    setLoading(true);
    let query = '/purchases/orders?page=' + currentPage + '&page_size=' + pageSize;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(activeFilters).length) {
      query += '&' + new URLSearchParams(activeFilters).toString();
    }
    const data = await get(query);
    if (data?.orders?.data) {
      setRowData(data.orders.data);
      setTotalPages(data.orders.last_page);
      setTotalRecords(data.orders.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getOrders();
  }, [getOrders]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  const deleteOrder = async () => {
    const data = await del('/purchases/orders/delete/' + orderToDelete);
    if (data.success) {
      toast.success(data.message || t('purchases.orderDeleted'));
      getOrders();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setOrderToDelete(null);
  };

  const columnDefs = useMemo(() => [
    // Only worth a column when there is more than one branch to tell apart.
    ...(isMultiBranch || allBranchesMode ? [{
      field: 'branch.code',
      headerName: t('branches.branch'),
      minWidth: 110,
      sortable: true,
      valueGetter: (p) => p.data?.branch?.code ?? '-'
    }] : []),
    { field: 'order_number', headerName: t('purchases.orderNumber'), minWidth: 140, flex: 1, sortable: true },
    {
      headerName: t('purchases.supplier'),
      minWidth: 200,
      flex: 1,
      sortable: true,
      valueGetter: (p) => {
        const s = p.data.supplier;
        if (!s) return '-';
        return [s.firstname, s.lastname].filter(Boolean).join(' ') || s.company_name || '-';
      }
    },
    { field: 'order_date', headerName: t('purchases.orderDate'), width: 140, sortable: true, valueFormatter: (p) => formatDate(p.value) },
    { field: 'expected_delivery_date', headerName: t('purchases.expectedDelivery'), width: 140, sortable: true, valueFormatter: (p) => formatDate(p.value) },
    {
      field: 'status',
      headerName: t('common.status'),
      width: 120,
      sortable: true,
      cellRenderer: (params) => {
        return printOrderStatus(params.value);
      }
    },
    {
      field: 'total_amount', 
      headerName: t('common.amount'), 
      minWidth: 120, 
      sortable: true,
      valueFormatter: (p) => {
        if (p.value === null || p.value === undefined) return '-';
        return `${p.value} ${p.data.currency.toUpperCase()}`;
      }
    },
    {
      headerName: t('common.actions'),
      minWidth: 100,
      sortable: false,
      resizable: false,
      cellRenderer: ActionsCellRenderer,
    },
  ], [t, formatDate, isMultiBranch, allBranchesMode]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
  }), []);

  const gridContext = useMemo(() => ({ navigate, setOrderToDelete, setSelectedOrderId, setDrawerOpen }), [navigate]);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    setSelectedOrderId(event.data.id);
    setDrawerOpen(true);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t("purchases.orders")} onReload={getOrders} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t("common.filter")}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton
            color="primary"
            size="sm"
            onClick={() => navigate('/purchases/purchase-orders/create')}
          >
            {t("purchases.newOrder")}
          </DefaultButton>
          <Dropdown isOpen={moreOpen} toggle={() => setMoreOpen(o => !o)} size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ConfirmationModal
        isOpen={orderToDelete !== null}
        title={t("purchases.deleteOrder")}
        message={t("purchases.deleteOrderConfirm")}
        onConfirm={deleteOrder}
        toggle={() => setOrderToDelete(null)}
      />

      <ViewDrawer
        orderId={selectedOrderId}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onDelete={() => {
          setDrawerOpen(false);
          setOrderToDelete(selectedOrderId);
        }}
        refreshOrders={getOrders}
      />
    </SidebarLayout>
  );
}

export default OrdersPage;
